import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { BarChart3, Activity, FlaskConical } from 'lucide-react';
import { getAvailableAdminPages, ADMIN_ROLES, isAdminOnlyUser } from './types/roles';
import { useViewportHeight } from './hooks/useViewportHeight';
import { ProfileDropdown } from './components/ui/ProfileDropdown';
import { ProfileModal } from './components/ui/ProfileModal';
import { AppFeedbackModal } from './components/ui/AppFeedbackModal';
import clsx from 'clsx';

const AdminLayout: React.FC = () => {
  // Keep viewport height in sync with visible area (handles mobile keyboard)
  useViewportHeight();

  const location = useLocation();
  const { user } = useAuth();
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);

  // Get navigation items based on user roles
  const availablePages = getAvailableAdminPages(user?.roles);
  const adminOnly = isAdminOnlyUser(user?.roles);

  // Map icons to pages
  const getPageIcon = (role: string) => {
    switch (role) {
      case ADMIN_ROLES.REPORTS_READER:
        return BarChart3;
      case ADMIN_ROLES.SESSIONS_READER:
        return Activity;
      case ADMIN_ROLES.TEST_CONTRIBUTOR:
        return FlaskConical;
      default:
        return BarChart3;
    }
  };

  return (
    <div className="h-app-viewport bg-rm-bg-0 flex flex-col">
      {/* Admin Header - Darker theme to distinguish from user app */}
      <nav className="bg-rm-primary-700 text-white shadow-card border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Logo, Title, and Navigation Links */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rm-primary-700 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-rm-accent-400" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">HR</h1>
                  <p className="text-xs text-rm-primary-300">Admin & Analytics</p>
                </div>
              </div>

              {/* Desktop Navigation Links - Left aligned */}
              <div className="hidden md:flex items-center space-x-1">
                {availablePages.map((page) => {
                const Icon = getPageIcon(page.role);
                const isActive = location.pathname.startsWith(page.href);
                return (
                  <Link
                    key={page.href}
                    to={page.href}
                    className={clsx(
                      'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-rm-accent-500 text-rm-ink-900'
                        : 'text-white hover:text-rm-accent-300 hover:bg-white/10'
                    )}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {page.name}
                  </Link>
                );
              })}
              </div>
            </div>

            {/* Right: User Menu */}
            <div className="flex items-center gap-4">
              {/* Switch to User View - hidden for admin-only users */}
              {!adminOnly && (
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-rm-primary-200 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <img src="/KimAvatar.png" alt="Kim Avatar" className="h-7 w-7 rounded-full" />
                  <span className="hidden sm:inline">Kim</span>
                </Link>
              )}

              {/* Profile Dropdown - same component as user layout */}
              <ProfileDropdown
                onProfileClick={() => setIsProfileModalOpen(true)}
                onFeedbackClick={() => setIsFeedbackModalOpen(true)}
                showName
                additionalItems={(onClose) => (
                  <>
                    {/* Mobile Navigation */}
                    <div className="md:hidden py-1">
                      {availablePages.map((page) => {
                        const Icon = getPageIcon(page.role);
                        const isActive = location.pathname.startsWith(page.href);
                        return (
                          <Link
                            key={page.href}
                            to={page.href}
                            onClick={onClose}
                            className={clsx(
                              'flex items-center px-4 py-2.5 text-sm transition-colors',
                              isActive
                                ? 'bg-rm-accent-100 text-rm-ink-900 font-medium'
                                : 'text-rm-ink-900 hover:bg-rm-bg-50'
                            )}
                          >
                            <Icon className="w-4 h-4 mr-3 text-rm-neutral-500" />
                            {page.name}
                          </Link>
                        );
                      })}
                    </div>

                    {/* Switch to User View (mobile) - hidden for admin-only users */}
                    {!adminOnly && (
                      <Link
                        to="/"
                        onClick={onClose}
                        className="sm:hidden flex items-center gap-2 px-4 py-2.5 text-sm text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
                      >
                        <img src="/KimAvatar.png" alt="Kim" className="h-5 w-5 rounded-full" />
                        Kim
                      </Link>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 min-h-0 max-w-7xl mx-auto w-full px-4 sm:px-6 flex flex-col py-6">
        <Outlet />
      </main>

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />

      {/* App Feedback Modal */}
      <AppFeedbackModal 
        isOpen={isFeedbackModalOpen} 
        onClose={() => setIsFeedbackModalOpen(false)} 
      />
    </div>
  );
};

export default AdminLayout;